import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type CreateLabReceptionAddress,
  type LabReceptionAddress,
  type UpdateLabReceptionAddress
} from '../types/types'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'
import FetchError from '../FetchError'

const key = (labId: number) => ['lab-reception-addresses', labId]

export const useGetLabReceptionAddressesQuery = (labId?: number) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: key(labId ?? 0),
    enabled: labId != null,
    queryFn: async () => {
      const { data, error } = await authFetch().GET(
        '/labs/{labId}/reception-addresses',
        { params: { path: { labId: labId as number } } }
      )
      if (error) throwOnError(error)
      return data ?? []
    }
  })
}

export const useCreateLabReceptionAddressMutation = (labId: number) => {
  const { authFetch } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateLabReceptionAddress) => {
      const { data, error } = await authFetch().POST(
        '/labs/{labId}/reception-addresses',
        { params: { path: { labId } }, body }
      )
      if (error) throwOnError(error)
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key(labId) })
  })
}

type UpdateVariables = {
  addressId: number
  etag: string
  body: UpdateLabReceptionAddress
}

export const useUpdateLabReceptionAddressMutation = (labId: number) => {
  const { authFetch } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ addressId, etag, body }: UpdateVariables) => {
      const { data, error } = await authFetch().PUT(
        '/labs/{labId}/reception-addresses/{addressId}',
        {
          params: { path: { labId, addressId }, header: { 'If-Match': etag } },
          body
        }
      )
      if (error) throwOnError(error)
      return data
    },
    onError: (error) => {
      if (error instanceof FetchError && error.status === 412) {
        return queryClient.invalidateQueries({ queryKey: key(labId) })
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key(labId) })
  })
}

type DeleteVariables = { addressId: number; etag: string }

export const useDeleteLabReceptionAddressMutation = (labId: number) => {
  const { authFetch } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ addressId, etag }: DeleteVariables) => {
      const { error } = await authFetch().DELETE(
        '/labs/{labId}/reception-addresses/{addressId}',
        { params: { path: { labId, addressId }, header: { 'If-Match': etag } } }
      )
      if (error) throwOnError(error)
    },
    onError: (error) => {
      if (error instanceof FetchError && error.status === 412) {
        return queryClient.invalidateQueries({ queryKey: key(labId) })
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key(labId) })
  })
}

export type { LabReceptionAddress }
